// Селекторы для gameState

// Базовые селекторы
import {selectSelectedUnitId} from "../privateStateReducer/selectors";
import {getSelectedUnit} from "../../../utils/gameState/getSelectedUnit";
import {
  getActiveTurnTimeOverrideMs,
  getStandardTurnDurationMs,
  getTurnDurationMs,
  getTurnPendingPlayerIds
} from "../../../utils/phase/turnTimeLimits";

export const selectSharedState = (state) => state.shared;
export const selectSharedGameState = (state) => selectSharedState(state).gameState;
export const selectGridConfigState = (state) => selectSharedState(state).gridConfig;
export const selectGameType = (state) => selectSharedGameState(state).gameType;
// Планировщик ботов (волна 29): админский тумблер локальной партии
export const selectIsBotsPlannerModeEnabled = (state) =>
  Boolean(selectSharedGameState(state).plannerBotsEnabled);

export const selectPlayers = (state) => selectSharedGameState(state).players;
export const selectTurnEndTimestamp = (state) => selectSharedGameState(state).turnEndTimestamp;
export const selectGameStartedAt = (state) => selectSharedGameState(state).startedAt;
export const selectTimerEnabled = (state) => selectSharedGameState(state).timerEnabled;
export const selectFastMode = (state) => selectSharedGameState(state).fastMode;

// === Время хода (см. utils/phase/turnTimeLimits) ===

/**
 * Время текущего хода, которое показывать зрителю viewerId: кнопка завершения
 * хода, таймер локальной партии, сторож протухшего состояния. В обычной
 * партии — стандартное время фазы (getPhaseDurationMs). В промо-матче против
 * ИИ у агента свой предохранитель: его ход все видят с его временем, а на
 * одновременном этапе зритель, который сам еще должен действовать, видит свое
 * время. Без viewerId (или у закончившего и зрителя) — сколько ждать
 * последнего из тех, кого еще ждут
 */
export const selectTurnDurationMs = (state, viewerId) => getTurnDurationMs(
  selectSharedGameState(state),
  {viewerId}
);

// Стандартное время текущей фазы/этапа — одно для всех игроков
export const selectStandardTurnDurationMs = (state) => getStandardTurnDurationMs(selectSharedGameState(state));

// Промо-матч против ИИ (признак приходит с START_GAME и есть в записи матча)
export const selectIsAiMatch = (state) => Boolean(selectSharedGameState(state)?.isAiMatch);

/**
 * ИИ промо-матча, который сейчас обдумывает ход со своим увеличенным
 * временем: его ждут на текущем этапе и персональное время включено. На
 * автопилоте (время выключено) за ИИ ходит серверный бот по стандартному
 * времени — тогда null. Самому ИИ (viewerId — его id) тоже null: это взгляд
 * тех, кто его ждет, включая зрителей. Подсказки «ИИ думает» в интерфейсе
 * нет — селектор проверяет состояние таймеров в e2e (aiMatchTurnTimers,
 * aiPromoEdgeCases).
 *
 * Возвращает игрока из состояния — стабильную ссылку для useSelector
 */
export const selectAiThinkingPlayer = (state, viewerId) => {
  const gameState = selectSharedGameState(state);
  if (!gameState?.isStarted || (gameState.gameResults && gameState.gameResults.length > 0)) {
    return null;
  }
  const aiPlayers = (gameState.players || []).filter((player) => player.isAi && player.id !== viewerId);
  if (aiPlayers.length === 0) {
    return null;
  }
  const pendingIds = getTurnPendingPlayerIds(gameState);
  return aiPlayers.find((player) => pendingIds.includes(player.id)
    && getActiveTurnTimeOverrideMs(gameState, player.id) !== null) || null;
};
// Подложка поля (см. configs/groundBackgrounds): по ней GameScene грузит РОВНО
// одну картинку пейзажа из перечня
export const selectGroundBackground = (state) => selectSharedGameState(state).groundBackground;
export const selectPolicyMarket = (state) => selectSharedGameState(state).policyMarket;
export const selectCurrentPlayerIndex = (state) => selectSharedGameState(state).currentPlayerIndex;
export const selectPhaseCompletedPlayers = (state) => selectSharedGameState(state).phaseCompletedPlayers;
export const selectCurrentPlayer = (state) => {
  const players = selectPlayers(state);
  if (!players) return null;
  const index = selectCurrentPlayerIndex(state);
  return players[index] || null;
};
export const selectPhase = (state) => selectSharedGameState(state).phase;
export const selectRound = (state) => selectSharedGameState(state).round;
export const selectFinalRound = (state) => selectSharedGameState(state).finalRound;
export const selectIsGameStarted = (state) => selectSharedGameState(state).isStarted;

export const selectStartingQuarters = (state) => selectSharedGameState(state).startingQuarters;
export const selectPlayersConfirmedSetupPhase = (state) => selectSharedGameState(state).playersConfirmedSetupPhase;

// Селекторы состояния игрового поля
export const selectHexes = (state) => selectSharedState(state).hexes;

// Селекторы для этапа войны
export const selectWarPhaseStage = (state) => selectSharedGameState(state).warPhaseStage;

// Селекторы для отрядов
export const selectUnits = (state) => selectSharedGameState(state).units;
export const selectSelectedUnit = (state) => {
  const units = selectUnits(state);
  const selectedId = selectSelectedUnitId(state);
  return getSelectedUnit(units, selectedId) || null;
};

// Селекторы для отрядов игрока
export const selectPlayerUnits = (state, playerId) => {
  const units = selectUnits(state);
  return units.filter(u => u.ownerId === playerId);
};

export const selectCurrentPlayerUnits = (state) => {
  const currentPlayer = selectCurrentPlayer(state);
  if (!currentPlayer) return [];
  return selectPlayerUnits(state, currentPlayer.id);
};

// Селекторы для городов
const EMPTY_CITIES = [];

export const selectCitiesCache = (state) => selectSharedGameState(state).citiesCache;
export const selectCurrentPlayerCities = (state) => {
  const isStarted = selectIsGameStarted(state);
  if (!isStarted) return null;
  const citiesCache = selectCitiesCache(state);
  const currentPlayer = selectCurrentPlayer(state);
  if (!currentPlayer) return EMPTY_CITIES;
  return citiesCache[currentPlayer.color] || EMPTY_CITIES;
}

export const selectConstructionMarkersVisible = (state) =>
  selectSharedGameState(state).constructionMarkersVisible;

// Селекторы для отложенных строений (фаза строительства)
export const selectPendingBuildings = (state) => selectSharedGameState(state).pendingBuildings;
export const selectInvalidPendingBuildings = (state) => selectSharedGameState(state).invalidPendingBuildings;
export const selectFortressesUnderConstruction = (state) => selectSharedGameState(state).fortressesUnderConstruction;
export const selectElementalsConstructionTokens = (state) => selectSharedGameState(state).elementalsConstructionTokens;
const EMPTY_ELEMENTALS_PLAN_TOKENS_BY_PLAYER_ID = Object.freeze({});
export const selectElementalsPlanTokensByPlayerId = (state) => (
  selectSharedGameState(state).elementalsPlanTokensByPlayerId || EMPTY_ELEMENTALS_PLAN_TOKENS_BY_PLAYER_ID
);

// Селекторы для отложенных действий найма (фаза войны - набор)
export const selectPendingRecruitmentActions = (state) => selectSharedGameState(state).pendingRecruitmentActions;

// Селекторы для отложенных действий перемещения (фаза войны - перемещение)
export const selectPendingMovementActions = (state) => selectSharedGameState(state).pendingMovementActions;
export const selectLeprechaunsStealCoinsActions = (state) => selectSharedGameState(state).leprechaunsStealCoinsActions;
export const selectSuccubesSeductionActions = (state) => selectSharedGameState(state).succubesSeductionActions;
export const selectGoblinsTraps = (state) => selectSharedGameState(state).goblinsTraps;
export const selectGoblinsAbilityActions = (state) => selectSharedGameState(state).goblinsAbilityActions;
export const selectPendingGoblinsTrapKillResults = (state) => selectSharedGameState(state).pendingGoblinsTrapKillResults;
// Все срабатывания ловушек за текущий этап перемещения/маневра — источник и
// для картинки на поле (updateGoblinsTrapTriggerDisplay), и для правила «на
// ячейку погибшего не встать»
export const selectGoblinsTrapStageTriggers = (state) => selectSharedGameState(state).goblinsTrapStageTriggers || [];

// Ячейки, где на этом этапе перемещения/маневра отряды погибли от ловушек: на
// них нельзя встать до конца этапа (сквозь — можно), см.
// calculateAvailableMovementCells. Кэш на одну запись: массив пересобирается
// только когда меняется сам список срабатываний, иначе каждый вызов давал бы
// новый массив на ровном месте (а зовут его на каждую перерисовку маркеров)
let cachedDeathHexKeys = {triggers: null, hexKeys: []};
export const selectGoblinsTrapDeathHexKeys = (state) => {
  const triggers = selectGoblinsTrapStageTriggers(state);
  if (cachedDeathHexKeys.triggers !== triggers) {
    cachedDeathHexKeys = {
      triggers,
      hexKeys: [...new Set(triggers.filter((trigger) => trigger.died).map((trigger) => trigger.finalHexKey))],
    };
  }
  return cachedDeathHexKeys.hexKeys;
};

export const selectPendingSupply = (state) => selectSharedGameState(state).pendingSupply;

// Селекторы для отложенных действий стрельбы (фаза войны - стрельба)
export const selectPendingShootingActions = (state) => selectSharedGameState(state).pendingShootingActions;

export const selectPendingMeleeDamage = (state) => selectSharedGameState(state).pendingMeleeDamage;
export const selectPlayersWaitedToConfirmCombatStage = (state) => selectSharedGameState(state).playersWaitedToConfirmCombatStage;
export const selectPlayersConfirmedCombatStage = (state) => selectSharedGameState(state).playersConfirmedCombatStage;

// Селекторы для отложенных действий маневра (фаза войны - маневр)
export const selectPendingManeuverActions = (state) => selectSharedGameState(state).pendingManeuverActions;

// Получить цвет игрока по ID
export const selectPlayerById = (state, playerId) => {
  const players = selectPlayers(state);
  return players.find(p => p.id === playerId) || null;
};

// Получить цвет игрока по ID (alias)
export const selectPlayerColorById = (state, playerId) => {
  const player = selectPlayerById(state, playerId);
  return player?.color || null;
};

// Селектор для результатов игры (заполняется в конце игры)
export const selectGameResults = (state) => selectSharedGameState(state).gameResults;
export const selectGameEndReason = (state) => selectSharedGameState(state).gameEndReason || null;
// Раунд конца партии. Нуля и мусора не отдает: состояние могло прийти со
// старого сервера (в снимке поля нет), а нулевой раунд технически достижим —
// первый переход расстановки в снабжение считается концом раунда
export const selectGameEndRound = (state) => {
  const gameEndRound = selectSharedGameState(state).gameEndRound;
  return Number.isFinite(gameEndRound) && gameEndRound > 0 ? gameEndRound : null;
};

// Селектор для расширенных результатов игры (с изменением рейтинга и наградами)
export const selectExtendedGameResults = (state) => selectSharedGameState(state).extendedGameResults;
export const selectIsRankedMatch = (state) => selectSharedGameState(state).isRanked;

// Селектор для захваченных городов в последнем раунде
export const selectLastCapturedCities = (state) => selectSharedGameState(state).lastCapturedCities;
// Сводка последнего захвата по игрокам (см. buildCityCaptureStats)
export const selectLastCityCaptureStats = (state) => selectSharedGameState(state).lastCityCaptureStats;
export const selectCombatResults = (state) => selectSharedGameState(state).combatResults;
export const selectCombatStats = (state) => selectSharedGameState(state).combatStats;

// Переписка игроков матча (dev-функция, см. ADD_CHAT_MESSAGE)
export const selectChatMessages = (state) => selectSharedGameState(state).chatMessages || [];

// Селектор для специальных строений
export const selectSpecialBuildings = (state) => selectSharedGameState(state).specialBuildings;

export const selectBuildingsDamage = (state) => selectSharedGameState(state).buildingsDamage;

export const selectShowUnitUpgradeModal = (state) => selectSharedGameState(state).showUnitUpgradeModal
export const selectUnitForUpgradeId = (state) => selectSharedGameState(state).unitForUpgradeId

// === Initiative interception selectors ===
export const selectNextInitiativePlayerId = (state) => selectSharedGameState(state).nextInitiativePlayerId;
export const selectTurnOrder = (state) => selectSharedGameState(state).turnOrder;
// POLICIES
export const selectPolicyAgrarianism = (state) => selectSharedState(state).policyAgrarianism
// Выбор к обмену по аграризму хранится по игрокам (см. policyAgrarianismReducer).
// Без playerId — выбор текущего игрока: так читали его всегда, пока снабжение
// раздавал только ходящий. В одновременной фазе снабжения передавайте игрока явно
const EMPTY_FARMS_FOR_CONVERSION = [];
const selectPlayerAgrarianism = (state, playerId) => {
  const resolvedPlayerId = playerId === undefined ? selectCurrentPlayer(state)?.id : playerId;
  return selectPolicyAgrarianism(state)?.byPlayerId?.[resolvedPlayerId] || null;
};
export const selectIsAgrarianismConversionOn = (state, playerId) => Boolean(
  selectPlayerAgrarianism(state, playerId)?.isAgrarianismConversionOn
);
export const selectSelectedFarmsForConversion = (state, playerId) => (
  selectPlayerAgrarianism(state, playerId)?.selectedFarmsForConversion || EMPTY_FARMS_FOR_CONVERSION
);

export const selectIsInitiativeInterceptedWithIsolationism = (state) => selectSharedGameState(state).isInitiativeInterceptedWithIsolationism

// FACTIONS: GIANTS
export const selectGiantsAdditionalSupply = (state) => selectSharedState(state).giantsAdditionalSupply;
// Без playerId — выбор текущего игрока (см. selectSelectedFarmsForConversion)
export const selectGiantsSelectedUnitId = (state, playerId) => {
  const resolvedPlayerId = playerId === undefined ? selectCurrentPlayer(state)?.id : playerId;
  return selectGiantsAdditionalSupply(state)?.selectedUnitIdByPlayerId?.[resolvedPlayerId] ?? null;
};
// Одновременная фаза снабжения сетевой игры (см. utils/gameState/supply/parallelSupply)
export const selectIsParallelSupply = (state) => Boolean(selectSharedGameState(state).isParallelSupply);
